import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useRoutes } from 'react-router-dom';
import { selectFlow } from '../store/app';
import { pagesRoutes, type RoutesProps } from './pagesConfig';
import { useCookieBanner, useSendEvents } from '../hooks';
import { EVENTS as E } from '../constants';
import { LegalFooter, MainLayout } from '../../components';
import { parseOnboardingPath } from '../../locales/languageConfig';
import * as S from './styled';

interface RouteInMainLayoutProps extends RoutesProps {}

export const RoutesInMainLayout = ({
  path,
  element,
  withoutWrap,
  withButton,
  withoutFooter,
  wrapperProps = {},
}: RouteInMainLayoutProps) => {
  const sendEvents = useSendEvents();
  const flow = useSelector(selectFlow);
  const initEventSentRef = useRef(false);

  useEffect(() => {
    if (initEventSentRef.current || flow?.indexOf(path.replace('/', '')) !== 0) return;
    initEventSentRef.current = true;
    sendEvents(E.ONBOARDING_START);
  }, [sendEvents, flow, path]);

  useCookieBanner(path);

  const content = withoutWrap ? element : <MainLayout {...wrapperProps}>{element}</MainLayout>;

  if (path === '/*') {
    return content;
  }

  return (
    <S.Page>
      {withoutWrap ? <S.Main>{content}</S.Main> : content}
      {!withoutFooter && (
        <LegalFooter
          screenId={element.props.screenId ?? path.slice(1)}
          pb={withButton ? 140 : undefined}
        />
      )}
    </S.Page>
  );
};

export const Router = () => {
  const location = useLocation();
  const { pathname, search } = location;
  const navigate = useNavigate();
  const cleanPath = parseOnboardingPath(pathname).cleanPath;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const handleBackButton = (event: PopStateEvent) => {
      event.preventDefault();
      navigate(`${pathname}${search}`, { replace: true });
    };

    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [pathname, search, navigate]);

  const wrappedRoutes = pagesRoutes.map((route) => ({
    ...route,
    element: <RoutesInMainLayout {...route} />,
  }));

  return useRoutes(wrappedRoutes, {
    ...location,
    pathname: cleanPath,
  });
};
